import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from 'chart.js'

// Register all Chart.js components
Chart.register(...registerables)

export default class extends Controller {
  static values = { 
    data: Object,
    type: { type: String, default: 'line' },
    options: Object,
    responsive: { type: Boolean, default: true }
  }
  
  connect() {
    // Wait for Chartkick to render the canvas element
    this.waitForCanvas()

    console.log("Chart controller connected")
  }
  
  waitForCanvas() {
    // Look for canvas element within this controller's element
    const canvas = this.element.querySelector('canvas')
    
    if (canvas) {
      this.canvasElement = canvas
      this.initializeChart()
    } else {
      // If canvas not found, wait a bit and try again (Chartkick might still be rendering)
      this.canvasTimeout = setTimeout(() => this.waitForCanvas(), 100)
    }
  }
  
  disconnect() {
    // Clear any pending timeout
    if (this.canvasTimeout) {
      clearTimeout(this.canvasTimeout)
    }
    
    if (this.chart) {
      this.chart.destroy()
    }
  }
  
  initializeChart() {
    if (!this.canvasElement) {
      console.error('Canvas element not found for chart controller')
      return
    }
    
    console.log('Initializing chart with:', {
      type: this.typeValue,
      data: this.dataValue,
      options: this.optionsValue
    })
    
    const ctx = this.canvasElement.getContext('2d')
    
    this.chart = new Chart(ctx, {
      type: this.typeValue,
      data: this.dataValue,
      options: this.optionsValue || this.buildOptions()
    })
    
    console.log('Chart initialized successfully')
  }
  
  buildOptions() {
    const defaultOptions = {
      responsive: this.responsiveValue,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: {
            color: '#9CA3AF', // gray-400
            font: {
              family: 'system-ui'
            }
          }
        },
        tooltip: {
          backgroundColor: '#1F2937', // gray-800
          titleColor: '#F3F4F6', // gray-100
          bodyColor: '#D1D5DB', // gray-300
          borderColor: '#374151', // gray-700
          borderWidth: 1
        }
      },
      scales: this.buildScales()
    }
    
    // Merge with provided options
    return this.deepMerge(defaultOptions, this.optionsValue || {})
  }
  
  buildScales() {
    const scales = {}
    
    if (this.typeValue === 'line' || this.typeValue === 'bar') {
      scales.x = {
        grid: {
          color: '#374151', // gray-700
          borderColor: '#4B5563' // gray-600
        },
        ticks: {
          color: '#9CA3AF' // gray-400
        }
      }
      
      scales.y = {
        grid: {
          color: '#374151', // gray-700
          borderColor: '#4B5563' // gray-600
        },
        ticks: {
          color: '#9CA3AF' // gray-400
        }
      }
    }
    
    return scales
  }
  
  // Update chart data dynamically
  updateData(newData) {
    if (!this.chart) return
    
    this.chart.data = newData
    this.chart.update()
  }
  
  // Update chart type
  updateType(newType) {
    if (!this.chart) return
    
    this.chart.destroy()
    this.typeValue = newType
    this.initializeChart()
  }
  
  // Utility method to deep merge objects
  deepMerge(target, source) {
    const output = Object.assign({}, target)
    
    if (this.isObject(target) && this.isObject(source)) {
      Object.keys(source).forEach(key => {
        if (this.isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] })
          } else {
            output[key] = this.deepMerge(target[key], source[key])
          }
        } else {
          Object.assign(output, { [key]: source[key] })
        }
      })
    }
    
    return output
  }
  
  isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item)
  }
  
  // Handle value changes
  dataValueChanged() {
    if (this.chart) {
      this.updateData(this.dataValue)
    }
  }
  
  typeValueChanged() {
    if (this.chart) {
      this.updateType(this.typeValue)
    }
  }
} 